/**
 * Action columns for the admin tables.
 *
 * One convention, everywhere: an action cell is a HIT TARGET, not an icon
 * floating in padding. The column is narrow and unpadded, and the control
 * stretches to fill the cell edge to edge with no border radius — so the whole
 * cell is clickable and the column reads as part of the table's grid rather than
 * a row of buttons sitting on top of it.
 *
 * With more than one control, the cell is a full-size `Flx` and each child grows
 * to an equal share of the width at full height. Two buttons therefore split the
 * cell in half rather than clustering at one end.
 *
 * See ADMIN_UI.md for the rule and the rest of the admin table conventions.
 */
import PropTypes from "prop-types";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteOutline from "@mui/icons-material/DeleteOutline";

import Flx from "../../../components/layout/Flx";

/** Width of one square action control — keep column widths a multiple of it. */
export const ACTION_W = 36;

/**
 * Wrapper for an action cell's controls. Fills the cell, splits the width
 * evenly, and squares off every child so they tile the cell with no gaps.
 *
 * The child selectors are DOUBLED (`&&`) on purpose. A MUI IconButton carries
 * its own `border-radius: 50%` and padding from a single-class rule; a plain
 * `& > *` here has the same specificity, so which one wins comes down to
 * stylesheet order — which emotion does not guarantee. `&&` doubles this rule's
 * specificity and settles it without reaching for `!important`.
 */
export const ActionCell = ({ children }) => (
  <Flx
    sx={{
      width: "100%",
      height: "100%",
      alignItems: "stretch",
      "&& > *": {
        flex: 1,
        minWidth: 0,
        width: "100%",
        height: "100%",
        borderRadius: 0,
        padding: 0,
      },
    }}
  >
    {children}
  </Flx>
);

ActionCell.propTypes = { children: PropTypes.node };

/**
 * Base column def for any action column. `count` sizes the column to the number
 * of controls it holds; pass `width` to override for text buttons.
 */
export const actionColumn = ({
  colId = "actions",
  headerName = "",
  count = 1,
  width,
  cellRenderer,
}) => ({
  headerName,
  colId,
  width: width ?? ACTION_W * count,
  sortable: false,
  resizable: false,
  pinned: "right",
  cellClass: "act",
  headerClass: "act",
  // The control reaches the cell edges: no padding, and a flex box so a
  // full-height child actually stretches. The pricing grid's `.act` class says
  // the same thing in CSS — setting it here too is what lets the column keep the
  // convention in an admin grid that doesn't carry that stylesheet.
  cellStyle: { padding: 0, display: "flex", alignItems: "stretch" },
  cellRenderer,
});

/** The row-delete column — the canonical single-control action cell. */
export const removeColumn = (onRemove, { headerName = "", tooltip = "Remove" } = {}) =>
  actionColumn({
    colId: "remove",
    headerName,
    cellRenderer: (p) => (
      <ActionCell>
        <Tooltip title={tooltip}>
          <IconButton size="small" onClick={() => onRemove(p)}>
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      </ActionCell>
    ),
  });
